/**
 * ADR-0159 — reading a refused write, and shaping a layout write.
 *
 * Both editors (pipelines and pages) present the version they were computed
 * against on every spec save, and both get the same 409 back when that version
 * has moved. The parsing lives here, not in the route files, so the shape the
 * hub actually sends is pinned by tests rather than by two hand-written copies
 * that agree today.
 *
 * Note what is deliberately NOT here: the map from a write source to its human
 * name. The hub serves `source_display` with the conflict body, so there is no
 * second vocabulary on this side to drift from the one the hub stamps.
 */

import { ApiRequestError } from "./api";

export type WriteConflict = {
  /** The version the object is on now — what a reload would land on. */
  updatedAt: string;
  /** A person's display name. Never an id, never an email we invented. */
  actorLabel: string;
  /** "Atlas agent", "the pipeline editor", … — served by the hub. */
  sourceDisplay: string;
  at: string;
};

/**
 * A `stale_write` 409, read into what the banner needs — or null for every
 * other failure, which the caller must go on handling as it did before.
 *
 * Tolerant on purpose: a conflict that fails to parse must still produce a
 * banner, because the alternative is a tab that silently stops saving. Every
 * field falls back to something a person can read.
 */
export function readWriteConflict(err: unknown): WriteConflict | null {
  if (!(err instanceof ApiRequestError) || err.code !== "stale_write") return null;
  const data = err.data as { current?: unknown } | undefined;
  const current = (isRecord(data?.current) ? data!.current : {}) as Record<string, unknown>;
  const lastWrite = (isRecord(current.last_write) ? current.last_write : {}) as Record<string, unknown>;
  const updatedAt = typeof current.updated_at === "string" ? current.updated_at : "";
  const at = typeof lastWrite.at === "string" ? lastWrite.at : updatedAt;
  return {
    updatedAt,
    actorLabel: nonEmpty(lastWrite.actor_label) ?? "Someone",
    sourceDisplay: nonEmpty(lastWrite.source_display) ?? "another surface",
    at,
  };
}

/**
 * ADR-0159 §3.4 — the layout channel's payload: every step's position, keyed
 * by step id. A full replace, so a step dropped from the graph simply stops
 * appearing here.
 */
export function layoutFromPositions(
  nodes: Array<{ id: string; position: { x: number; y: number } }>
): { nodes: Record<string, { x: number; y: number }> } {
  const out: Record<string, { x: number; y: number }> = {};
  for (const node of nodes) out[node.id] = { x: node.position.x, y: node.position.y };
  return { nodes: out };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function nonEmpty(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}
